
import AppToolbar from '@/_components/bar/appToolbar/appToolbar.vue'
import ProjectMixin from '@/_mixins/project'
import { Component } from 'vue-property-decorator'
import {
    getRequest,
    postRequest,
    patchRequest,
    patchRequestV2,
    deleteRequest,
} from '@/plugins/api-axios'
import { Mixins } from 'vue-mixin-decorator'
import { z } from 'zod'
import { serialise, deserialise } from 'kitsu-core'
import FormRevision from '@/views/Project/Form/components/formRevision.vue'
import { openDialog } from '@/_components/dialog/dialog'
import ConfirmationDialog from '@/views/Project/Form/dialogs/confirmation.vue'
import DialogTemplate from '@/_components/dialog/template/template.vue'
import AddButton from '@/_components/button/add/add.vue'
import FormRevisionsCard from './components/formRevisionsCard.vue'
import CopyFormDialog from '@/views/Project/Form/dialogs/copyFormDialog.vue'
import type MixinInterface from '@/_mixins'

const formKind = ['intakeForm', 'riskAssessment', 'selfReport', 'copyAnother'] as const

type FormKind = (typeof formKind)[number]

let FormSchema = z.object({
    id: z.string().optional(),
    kind: z.enum(formKind),
    name: z.string(),
    autoDeleteResponsesAfterDays: z.number().nullish(),
    requireApproval: z.boolean(),
    companyAdminsCanViewResponses: z.boolean(),
    companyAdminsCanCreateResponses: z.boolean(),
    isArchived: z.boolean(),
    isDeleted: z.boolean(),
    updatedAt: z.string().optional(),
})

let NewFormSchema = FormSchema.omit({ id: true })

let EditFormSchema = FormSchema.omit({ kind: true, updatedAt: true })

type Form = z.infer<typeof FormSchema>

@Component({
    mixins: [ProjectMixin],
    components: {
        AddButton,
        AppToolbar,
        CopyFormDialog,
        DialogTemplate,
        FormRevision,
        FormRevisionsCard,
    },
})
export default class ContractorForms extends Mixins<MixinInterface>(ProjectMixin) {
    isFormValid = false
    formDialogVisible = false
    formRevisionDialogVisible = false
    form = null as null | Form
    formHasChanged = false
    formPending = false
    contractorFormList = [] as Form[]
    isFetchingFormRevision = false
    formRevision = null
    formToRevisions = {}
    requiresValidationForActivation = false
    isCopyFormDialogVisible = false
    infiniteId = 0
    pageOffset = 0
    pageLimit = 15
    showCopyFormHelpText = true
    includeArchived = false

    get formKinds() {
        return formKind.map((kind) => {
            if (kind === 'copyAnother')
                return { value: kind, title: this.$t('form_fe["btn_copyAnother"]') }
            return { value: kind, title: this.$t(`contractor_enums["form.${kind}"]`) }
        })
    }

    get formRules() {
        return {
            name: [
                (v) => !!v || this.$t('fe_validation.error_required').toString(),
                (v) => v.length <= 100,
            ],
        }
    }

    handleIncludeArchivedChanged() {
        this.pageOffset = 0
        this.infiniteId = Date.now()
        this.contractorFormList = []
        this.formIds.clear()
    }

    async getForms(contractorFormIds: Array<string | undefined>) {
        if (!contractorFormIds.length) return

        let { data } = await getRequest(
            this.getURL({
                path: `forms`,
                base: this.formBaseUrl,
                params: {
                    'filter[resource]': contractorFormIds.filter(Boolean).join(','),
                    'page[limit]': contractorFormIds.length,
                    include: 'resource,activeRevision,inactiveRevisions,draftRevisions',
                },
            })
        )

        let formData = deserialise(data).data
        formData.forEach((form) => {
            this.$set(this.formToRevisions, form.resource.data.id, {
                id: form.id,
                updatedAt: form.updatedAt,
                data: this.flattenRevisions(form),
            })
        })
    }

    flattenRevisions(data: any) {
        let revisions: any[] = []

        if (data.activeRevision.data) {
            revisions.push({ ...data.activeRevision.data, _status: 'active' })
        }

        data.draftRevisions.data
            .sort((a: any, b: any) => b.version - a.version)
            .forEach((revision: any) => {
                revisions.push({ ...revision, _status: 'draft' })
            })

        data.inactiveRevisions.data.forEach((revision: any) => {
            revisions.push({ ...revision, _status: 'inactive' })
        })

        return revisions
    }

    async activateRevision(contractorForm: any, revision: any) {
        if (!revision?.id || !contractorForm.id) return

        let form = this.formToRevisions[contractorForm.id]
        let payload = serialise('form', {
            id: form.id,
            activeRevision: {
                data: { type: 'formRevision', id: revision.id },
            },
        })

        try {
            const response = await patchRequestV2(
                this.getURL({
                    path: `forms/${form.id}`,
                    base: this.formBaseUrl,
                }),
                payload.data,
                {
                    validateStatus: () => true, // manually handle response
                }
            )
            let hasErrors = response.data.errors?.length > 0

            if (!hasErrors) {
                this.getForms([contractorForm.id])
                return
            }

            let isRevisionDataInvalidForActivation = response.data.errors.some(
                (error: any) => error.code === 'revisionDataInvalidForActivation'
            )

            if (!isRevisionDataInvalidForActivation) {
                throw response
            }

            // if revision data is invalid for activation, show dialog
            this.viewRevision(contractorForm, revision)
            this.requiresValidationForActivation = true
        } catch (error: any) {
            if (error.data.errors) {
                this.$store.commit('setErrorSnackBar', {
                    data: error.data.errors,
                    text: this.$i18n.t('vbit_error.default_header'),
                })
            }
        }
    }

    async deleteRevision(form: Form, revision: any) {
        let ok = await openDialog(ConfirmationDialog, {
            message: this.$t('fe_contractors["form.revision.text_deleteRevisionConfirmation"]'),
            title: this.$t('fe_contractors["form.revision.heading_deleteRevision"]'),
        })

        if (!ok) return

        await deleteRequest(
            this.getURL({
                path: `formRevisions/${revision.id}`,
                base: this.formBaseUrl,
            }),
            {}
        )

        this.getForms([form.id])
    }

    newForm(kind: FormKind) {
        if (kind === 'copyAnother') {
            this.isCopyFormDialogVisible = true
            return
        }

        this.viewFormDetails({
            kind,
            name: '',
            requireApproval: false,
            companyAdminsCanViewResponses: false,
            companyAdminsCanCreateResponses: false,
            isArchived: false,
            isDeleted: false,
        })
    }

    async addOrEditForm() {
        if (!this.isFormValid) return

        try {
            this.formPending = true
            if (this.form?.id) {
                await this.editForm(this.form)
            } else {
                await this.addForm()
            }
        } finally {
            this.formPending = false
            this.formDialogVisible = false
        }
    }

    async addForm() {
        let payload = serialise('contractorForm', {
            ...NewFormSchema.parse(this.form),
            project: {
                data: {
                    type: 'project',
                    id: this.currentProjectId,
                },
            },
        })

        let { data } = await postRequest(
            this.getURL({ path: 'contractorForms', base: this.contractorBaseUrl }),
            {},
            payload.data
        )

        let newForm = deserialise(data).data
        await this.handleNewFormAdded(newForm)
        this.viewRevision(newForm)
    }

    async editForm(form: Form, attrs: Partial<Form> = {}) {
        this.form = { ...form, ...attrs }

        let payload = serialise('contractorForm', EditFormSchema.parse(this.form))

        let { data } = await patchRequest(
            this.getURL({
                path: `contractorForms/${payload.data.id}`,
                base: this.contractorBaseUrl,
            }),
            {},
            payload.data
        )

        if ((attrs.isArchived === true && !this.includeArchived) || attrs.isDeleted) {
            // filter out the form from the list
            this.contractorFormList = this.contractorFormList.filter(
                (form) => form.id !== payload.data.id
            )
            this.pageOffset = 0
            this.infiniteId = Date.now()
            return
        }

        this.contractorFormList = this.contractorFormList.map((form) => {
            if (form.id !== payload.data.id) return form
            return deserialise(data).data
        })
    }

    unwatchForm: (() => void) | undefined = undefined

    viewFormDetails(form: Form) {
        this.formHasChanged = false
        this.unwatchForm?.()

        this.form = { ...form }
        this.unwatchForm = this.$watch('form', () => (this.formHasChanged = true), { deep: true })
        this.formDialogVisible = true
    }

    async addRevision(form: Form) {
        if (!form.id) return

        let revision = {}
        let activeOrLatestDraft = this.formToRevisions[form.id]?.data[0]

        if (activeOrLatestDraft) {
            revision.data = { ...activeOrLatestDraft.data }
        }
        this.viewRevision(form, revision)
    }

    viewRevision(form: Form, revision: any = {}) {
        this.form = JSON.parse(JSON.stringify(form))
        this.formRevision = JSON.parse(JSON.stringify(revision))
        this.formRevisionDialogVisible = true
    }

    async handleNewFormAdded(newForm: any) {
        await this.getForms([newForm.id])

        // insert new form at the top of the list
        this.contractorFormList.unshift(newForm)
        this.formIds.add(newForm.id)

        this.$nextTick(() => {
            const formEl = document.querySelector(`[data-form-id='${newForm.id}']`)
            if (!formEl) return
            ;(this.$refs.formListEl as HTMLElement)?.scrollTo({ top: 0 })
            const highlightClasses = ['tw-outline', 'tw-outline-blue-400', 'tw-outline-offset-2']
            formEl.classList.add(...highlightClasses)
        })

        // adjust page offset after adding new form
        this.pageOffset =
            Math.floor(this.contractorFormList.length / this.pageLimit) * this.pageLimit
    }

    async handleRevisionChanged(data: { autoActivate: boolean; revision: any; keepOpen: boolean }) {
        this.formRevisionDialogVisible = Boolean(data.keepOpen)
        this.requiresValidationForActivation = false
        if (data.autoActivate === true && data.revision && this.form?.id) {
            this.activateRevision(this.form, data.revision)
        } else {
            this.getForms([this.form?.id])
        }
    }

    async handleRevisionAdded(data: {
        autoActivate: boolean
        revision: any
        keepOpen: boolean
        preActivationValidate: boolean
    }) {
        if (this.form) {
            this.formRevisionDialogVisible = false
            this.requiresValidationForActivation = false
            await this.$nextTick()

            if (data.autoActivate === true && data.revision && this.form?.id) {
                this.activateRevision(this.form, data.revision)
            } else {
                this.getForms([this.form?.id])
                if (data.keepOpen) {
                    this.requiresValidationForActivation = data.preActivationValidate
                    this.viewRevision(this.form, data.revision)
                }
            }
        }
    }

    handleFormRevisionDialogClosed() {
        this.formRevisionDialogVisible = false
        this.requiresValidationForActivation = false
    }

    formIds = new Set<string>()
    async getContractorForms($state: any) {
        if (!this.currentProjectId) return

        let { data } = await getRequest(
            this.getURL({
                path: 'contractorForms',
                params: {
                    'filter[project]': this.currentProjectId,
                    'filter[isDeleted]': false,
                    'filter[isArchived]': this.includeArchived,
                    'page[offset]': this.pageOffset,
                    'page[limit]': this.pageLimit,
                    sort: '-updatedAt',
                },
                base: this.contractorBaseUrl,
            })
        )
        const forms = deserialise(data).data
        const ids: string[] = []
        forms.forEach((form) => {
            if (!this.formIds.has(form.id)) {
                this.formIds.add(form.id)
                ids.push(form.id)
            }
        })
        await this.getForms(ids)
        forms.forEach((form) => {
            if (ids.includes(form.id)) this.contractorFormList.push(form)
        })

        if (forms.length === this.pageLimit) {
            this.pageOffset += this.pageLimit
            $state.loaded()
        } else {
            $state.complete()
        }
    }

    created() {
        this.$watch('currentProjectId', () => {
            this.pageOffset = 0
            this.infiniteId = Date.now()
            this.contractorFormList = []
        })
    }
}
